<!--
  A RAIL OF PLATES — genres, and collections.

  Same rail as everything else on the page, but the tiles carry OUR OWN artwork:
  a generated environment still per genre and per collection, one unified moody
  aesthetic across the whole set, served from /browse/ in the app bundle. That
  answers the old objection (drawing a genre as one arbitrary title's cover is a
  lie about forty others) without giving up pictures: these stills REPRESENT the
  genre and belong to nobody's film.

  THE GRADIENT PLATE IS THE FALLBACK, not a leftover. The hue is hashed from the
  name, so Comedy is the same colour on every device, and a tile whose image is
  missing or fails to load simply shows the plate it always showed — no broken
  glyph, no layout shift, because the image is painted over the plate rather
  than in place of it.

  TEXT ON A PHOTOGRAPH NEEDS ITS OWN LEGIBILITY. The label sits on a scrim
  gradient built from near-black (the same move the billboard makes) plus a
  drop shadow, because the token contrast gate measures tokens on tokens and
  cannot vouch for text on an arbitrary picture.
-->
<script lang="ts">
  import { hue } from './hue';
  interface Plate {
    href: string;
    label: string;
    /** A quiet second line — what a collection actually contains. */
    note?: string;
    /** An environment still for the tile. Absent or failing → gradient plate. */
    image?: string;
  }

  interface Props {
    title: string;
    plates: Plate[];
  }

  let { title, plates }: Props = $props();



  /*
   * Images that failed to load, by URL. State rather than a DOM mutation so the
   * scrim disappears with its picture — a scrim over the gradient plate would
   * just be a smudge.
   */
  let failed = $state<ReadonlySet<string>>(new Set());
  function fail(src: string): void {
    failed = new Set(failed).add(src);
  }
</script>

<section class="plate-rail">
  <h2>{title}</h2>
  <div class="plates" data-dpad="row">
    {#each plates as plate (plate.href)}
      <a class="plate" href={plate.href} style:--h={hue(plate.label)}>
        {#if plate.image && !failed.has(plate.image)}
          <img
            class="art"
            src={plate.image}
            alt=""
            loading="lazy"
            decoding="async"
            onerror={() => fail(plate.image!)}
          />
          <span class="scrim" aria-hidden="true"></span>
        {/if}
        <span class="lbl">{plate.label}</span>
        {#if plate.note}<span class="note">{plate.note}</span>{/if}
      </a>
    {/each}
  </div>
</section>

<style>
  .plate-rail {
    margin-top: var(--space-6);
  }
  .plate-rail h2 {
    font-size: var(--text-lg);
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-tight);
    padding-inline: var(--page-pad);
    margin-bottom: var(--space-3);
  }
  .plates {
    display: flex;
    gap: var(--gap);
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-snap-type: x proximity;
    padding: 0 var(--page-pad) var(--space-2);
    scroll-padding-inline: var(--page-pad);
  }
  .plates::-webkit-scrollbar {
    display: none;
  }

  .plate {
    position: relative;
    overflow: hidden;
    flex: 0 0 auto;
    scroll-snap-align: start;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: 2px;
    width: 176px;
    /* 16:9, so a row of these sits at the same height as a wide-card rail. */
    aspect-ratio: 16 / 9;
    padding: var(--space-3);
    border-radius: var(--radius-xs);
    text-decoration: none;
    color: var(--text);
    /*
     * oklch holds every hue at one lightness, so no genre comes out as the
     * unreadable one — the same trick Avatar uses for names.
     */
    background:
      linear-gradient(160deg, oklch(0.42 0.09 var(--h)), oklch(0.26 0.06 var(--h)));
    box-shadow: var(--shadow-1);
    transition:
      transform 130ms var(--ease-out),
      box-shadow 130ms var(--ease-out);
  }
  .plate:hover,
  .plate:focus-visible {
    transform: translateY(-3px);
    box-shadow: var(--shadow-2);
  }
  .plate:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 3px;
  }

  /* The still covers the plate; the plate remains underneath as the fallback. */
  .art {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  /*
   * Bottom-weighted, built from near-black rather than any theme token on
   * purpose: the picture underneath is fixed dark artwork, not a themed
   * surface, so the scrim's job never changes with the theme.
   */
  .scrim {
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgb(6 6 8 / 0.82), rgb(6 6 8 / 0.28) 46%, transparent 72%);
  }
  .lbl,
  .note {
    /* Above the still and its scrim; the spans are otherwise unpositioned and
       would paint underneath the absolutely positioned image. */
    position: relative;
    text-shadow: 0 1px 2px rgb(0 0 0 / 0.62);
  }
  .lbl {
    font-size: var(--text-lg);
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-tight);
    line-height: 1.1;
  }
  .note {
    font-size: var(--text-2xs);
    color: color-mix(in oklab, var(--text) 78%, transparent);
    line-height: 1.3;
  }
  @media (prefers-reduced-motion: reduce) {
    .plate {
      transition: none;
    }
    .plate:hover,
    .plate:focus-visible {
      transform: none;
    }
  }
</style>
